'use client';

import { useRef, useEffect, useCallback, memo } from 'react';
import { useDrawingStore } from '@/features/drawing/model';
import { getModifierKey } from '@/shared/lib/hooks/usePlatform';

interface BrushPreviewProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  canvasTransform: { zoom: number; pan: { x: number; y: number } };
}

/**
 * Optimized brush preview component that updates via requestAnimationFrame
 * instead of triggering React re-renders on every mouse move.
 *
 * This component renders the brush circle that follows the cursor,
 * completely isolated from the main ControlEditor render cycle.
 */
export const BrushPreview = memo(function BrushPreview({
  containerRef,
  canvasTransform,
}: BrushPreviewProps) {
  const previewRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const cursorPositionRef = useRef<{ x: number; y: number } | null>(null);
  const isVisibleRef = useRef(false);
  const updatePreviewPositionRef = useRef<() => void>(() => {});
  const modifierKey = getModifierKey();

  // Get store state (read-only, no subscriptions to frequently changing state)
  const tool = useDrawingStore((s) => s.tool);
  const brushSettings = useDrawingStore((s) => s.brushSettings);
  const cloneSettings = useDrawingStore((s) => s.cloneSettings);
  const spacePressed = useDrawingStore((s) => s.modifiers.spacePressed);
  const ctrlPressed = useDrawingStore((s) => s.modifiers.ctrlPressed);
  const altPressed = useDrawingStore((s) => s.modifiers.altPressed);
  const isDrawing = useDrawingStore((s) => s.drawing.isDrawing);

  // Get tool color
  const getToolColor = useCallback(() => {
    switch (tool) {
      case 'pencil':
        return brushSettings.color;
      case 'eraser':
        return 'rgb(239, 68, 68)'; // red-500
      case 'clone':
        return 'rgb(59, 130, 246)'; // blue-500
      default:
        return 'rgb(107, 114, 128)'; // gray-500
    }
  }, [tool, brushSettings.color]);

  // Update preview position using direct DOM manipulation (no React re-render)
  const updatePreviewPosition = useCallback(() => {
    const preview = previewRef.current;
    if (!preview) return;

    const pos = cursorPositionRef.current;
    const visible = isVisibleRef.current;

    if (!pos || !visible || spacePressed || tool === 'text') {
      preview.style.display = 'none';
      return;
    }

    preview.style.display = 'block';

    const displaySize = brushSettings.size * canvasTransform.zoom;
    const color = getToolColor();
    const { x, y } = pos;

    // For clone tool with source set, we need to update both circles
    if (tool === 'clone' && cloneSettings.source) {
      // Clone tool preview is more complex, handled separately
      updateClonePreview(preview, x, y, displaySize, color);
    } else if (tool === 'clone' && !cloneSettings.source) {
      // Clone tool without source - show instruction
      updateCloneNoSourcePreview(preview, x, y, displaySize, color);
    } else {
      // Standard brush preview (pencil/eraser)
      updateStandardPreview(preview, x, y, displaySize, color);
    }
  }, [
    brushSettings.size,
    canvasTransform.zoom,
    spacePressed,
    tool,
    cloneSettings.source,
    getToolColor,
  ]);

  // Keep ref updated with latest function version
  useEffect(() => {
    updatePreviewPositionRef.current = updatePreviewPosition;
  }, [updatePreviewPosition]);

  const updateStandardPreview = (
    preview: HTMLDivElement,
    x: number,
    y: number,
    displaySize: number,
    color: string
  ) => {
    // Update main circle
    const circle = preview.querySelector('.brush-circle') as HTMLDivElement;
    if (circle) {
      circle.style.left = `${x - displaySize / 2}px`;
      circle.style.top = `${y - displaySize / 2}px`;
      circle.style.width = `${displaySize}px`;
      circle.style.height = `${displaySize}px`;
      circle.style.borderColor = color;
      circle.style.display = 'block';
    }

    // Update size label
    const sizeLabel = preview.querySelector('.size-label') as HTMLDivElement;
    if (sizeLabel) {
      if (ctrlPressed && !altPressed) {
        sizeLabel.style.display = 'block';
        sizeLabel.style.left = `${x}px`;
        sizeLabel.style.top = `${y + displaySize / 2 + 5}px`;
        sizeLabel.textContent = `${brushSettings.size}px`;
      } else {
        sizeLabel.style.display = 'none';
      }
    }

    // Hide clone-specific elements
    const cloneElements = preview.querySelectorAll('.clone-element');
    cloneElements.forEach((el) => {
      (el as HTMLElement).style.display = 'none';
    });
  };

  const updateCloneNoSourcePreview = (
    preview: HTMLDivElement,
    x: number,
    y: number,
    displaySize: number,
    color: string
  ) => {
    // Hide standard circle
    const circle = preview.querySelector('.brush-circle') as HTMLDivElement;
    if (circle) circle.style.display = 'none';

    // Show clone no-source preview
    const outerCircle = preview.querySelector('.clone-outer') as HTMLDivElement;
    const innerCircle = preview.querySelector('.clone-inner') as HTMLDivElement;
    const instruction = preview.querySelector(
      '.clone-instruction'
    ) as HTMLDivElement;

    if (outerCircle) {
      outerCircle.style.display = 'block';
      outerCircle.style.left = `${x - displaySize / 2}px`;
      outerCircle.style.top = `${y - displaySize / 2}px`;
      outerCircle.style.width = `${displaySize}px`;
      outerCircle.style.height = `${displaySize}px`;
      outerCircle.style.borderColor = color;
    }

    if (innerCircle) {
      innerCircle.style.display = 'block';
      innerCircle.style.left = `${x - displaySize / 2 + 3}px`;
      innerCircle.style.top = `${y - displaySize / 2 + 3}px`;
      innerCircle.style.width = `${displaySize - 6}px`;
      innerCircle.style.height = `${displaySize - 6}px`;
      innerCircle.style.borderColor = color;
    }

    if (instruction) {
      instruction.style.display = 'block';
      instruction.style.left = `${x}px`;
      instruction.style.top = `${y + displaySize / 2 + 8}px`;
    }
  };

  const updateClonePreview = (
    preview: HTMLDivElement,
    x: number,
    y: number,
    displaySize: number,
    color: string
  ) => {
    // Calculate source position
    let sourceX = x;
    let sourceY = y;

    if (cloneSettings.offset) {
      const offsetInScreen = {
        x: cloneSettings.offset.x * canvasTransform.zoom,
        y: cloneSettings.offset.y * canvasTransform.zoom,
      };
      sourceX = x + offsetInScreen.x;
      sourceY = y + offsetInScreen.y;
    }

    // Update source circle (dashed)
    const sourceCircle = preview.querySelector(
      '.clone-source'
    ) as HTMLDivElement;
    if (sourceCircle) {
      sourceCircle.style.display = 'block';
      sourceCircle.style.left = `${sourceX - displaySize / 2}px`;
      sourceCircle.style.top = `${sourceY - displaySize / 2}px`;
      sourceCircle.style.width = `${displaySize}px`;
      sourceCircle.style.height = `${displaySize}px`;
      sourceCircle.style.opacity = isDrawing ? '0.8' : '0.6';
    }

    // Update cross in source
    const crossV = preview.querySelector('.clone-cross-v') as HTMLDivElement;
    const crossH = preview.querySelector('.clone-cross-h') as HTMLDivElement;
    const crossSize = isDrawing ? 10 : 5;
    const crossWidth = isDrawing ? 3 : 2;

    if (crossV) {
      crossV.style.display = 'block';
      crossV.style.left = `${sourceX - 1}px`;
      crossV.style.top = `${sourceY - crossSize}px`;
      crossV.style.width = `${crossWidth}px`;
      crossV.style.height = `${crossSize * 2}px`;
      crossV.style.backgroundColor = isDrawing ? 'rgb(59, 130, 246)' : color;
      crossV.style.opacity = isDrawing ? '1' : '0.6';
      crossV.style.boxShadow = isDrawing
        ? '0 0 8px rgba(59, 130, 246, 0.8)'
        : 'none';
    }

    if (crossH) {
      crossH.style.display = 'block';
      crossH.style.left = `${sourceX - crossSize}px`;
      crossH.style.top = `${sourceY - 1}px`;
      crossH.style.width = `${crossSize * 2}px`;
      crossH.style.height = `${crossWidth}px`;
      crossH.style.backgroundColor = isDrawing ? 'rgb(59, 130, 246)' : color;
      crossH.style.opacity = isDrawing ? '1' : '0.6';
      crossH.style.boxShadow = isDrawing
        ? '0 0 8px rgba(59, 130, 246, 0.8)'
        : 'none';
    }

    // Update destination circle (solid)
    const destCircle = preview.querySelector('.clone-dest') as HTMLDivElement;
    if (destCircle) {
      destCircle.style.display = 'block';
      destCircle.style.left = `${x - displaySize / 2}px`;
      destCircle.style.top = `${y - displaySize / 2}px`;
      destCircle.style.width = `${displaySize}px`;
      destCircle.style.height = `${displaySize}px`;
      destCircle.style.opacity = isDrawing ? '1' : '0.9';
    }

    // Hide standard elements
    const circle = preview.querySelector('.brush-circle') as HTMLDivElement;
    if (circle) circle.style.display = 'none';

    const outerCircle = preview.querySelector('.clone-outer') as HTMLDivElement;
    const innerCircle = preview.querySelector('.clone-inner') as HTMLDivElement;
    const instruction = preview.querySelector(
      '.clone-instruction'
    ) as HTMLDivElement;
    if (outerCircle) outerCircle.style.display = 'none';
    if (innerCircle) innerCircle.style.display = 'none';
    if (instruction) instruction.style.display = 'none';
  };

  // Mouse move handler - updates position ref and schedules animation frame
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      cursorPositionRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };

      // Always mark as visible when mouse moves inside container
      isVisibleRef.current = true;

      // Schedule update on next animation frame (coalesces multiple moves)
      // Use ref to always call the latest version of updatePreviewPosition
      if (animationFrameRef.current === null) {
        animationFrameRef.current = requestAnimationFrame(() => {
          animationFrameRef.current = null;
          updatePreviewPositionRef.current();
        });
      }
    };

    const handleMouseLeave = () => {
      isVisibleRef.current = false;
      cursorPositionRef.current = null;
      if (previewRef.current) {
        previewRef.current.style.display = 'none';
      }
    };

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseleave', handleMouseLeave);

      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [containerRef]); // No longer depends on updatePreviewPosition

  // Update preview when tool/settings change (for cases where cursor doesn't move)
  useEffect(() => {
    updatePreviewPosition();
  }, [updatePreviewPosition, isDrawing]);

  const color = getToolColor();

  return (
    <div
      ref={previewRef}
      className="absolute inset-0 pointer-events-none z-10"
      style={{ display: 'none' }}
    >
      {/* Standard brush circle */}
      <div
        className="brush-circle pointer-events-none absolute rounded-full"
        style={{
          border: `2px solid ${color}`,
          display: 'none',
        }}
      />

      {/* Size label for Ctrl+hover */}
      <div
        className="size-label pointer-events-none absolute text-chip bg-black/70 text-white px-1 rounded"
        style={{
          transform: 'translateX(-50%)',
          display: 'none',
        }}
      />

      {/* Clone tool - no source set (two concentric dashed circles) */}
      <div
        className="clone-element clone-outer pointer-events-none absolute rounded-full"
        style={{
          border: `2px dashed ${color}`,
          display: 'none',
        }}
      />
      <div
        className="clone-element clone-inner pointer-events-none absolute rounded-full"
        style={{
          border: `2px dashed ${color}`,
          display: 'none',
        }}
      />
      <div
        className="clone-element clone-instruction pointer-events-none absolute text-chip bg-black/70 text-white px-2 py-1 rounded whitespace-nowrap"
        style={{
          transform: 'translateX(-50%)',
          display: 'none',
        }}
      >
        {modifierKey}+Click to select reference
      </div>

      {/* Clone tool - source set (source circle + cross + destination circle) */}
      <div
        className="clone-element clone-source pointer-events-none absolute rounded-full"
        style={{
          border: `2px dashed ${color}`,
          display: 'none',
        }}
      />
      <div
        className="clone-element clone-cross-v pointer-events-none absolute"
        style={{
          backgroundColor: color,
          display: 'none',
        }}
      />
      <div
        className="clone-element clone-cross-h pointer-events-none absolute"
        style={{
          backgroundColor: color,
          display: 'none',
        }}
      />
      <div
        className="clone-element clone-dest pointer-events-none absolute rounded-full"
        style={{
          border: `2px solid ${color}`,
          display: 'none',
        }}
      />
    </div>
  );
});
